<script setup lang="ts">
import { nextTick, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useMutation } from "@urql/vue";
import { replaceUserMergedConfig } from "@/init";

const { t } = useI18n();
const store = useStore();
const marker = "{{ link }}";
const keys = ["default_email_subject", "default_email_body"] as const;
type Field = (typeof keys)[number];
const effectiveValue = (key: Field) =>
  store.getters.user.mergedConfig?.[key] ?? "";
const fields = reactive(
  Object.fromEntries(
    keys.map((key) => [
      key,
      {
        draft: effectiveValue(key) as string,
        saved: effectiveValue(key) as string,
        message: "",
        error: false,
      },
    ]),
  ) as Record<
    Field,
    { draft: string; saved: string; message: string; error: boolean }
  >,
);
const pending = ref<Field | null>(null);
const bodyElement = ref<HTMLTextAreaElement | null>(null);
let selection = { start: 0, end: 0 };

// Obtain the effective configuration in the same mutation response, including
// inherited defaults after null/empty-string removal. Never merge null locally.
const { executeMutation } = useMutation<
  {
    updateUserConfigPartial: {
      userData: { mergedConfig: Record<string, unknown> };
    } | null;
  },
  { configPartial: Partial<Record<Field, string | null>> }
>(`
  mutation UpdateEmailPreset($configPartial: JSON!) {
    updateUserConfigPartial(input: { configPatch: $configPartial }) {
      userData { id mergedConfig }
    }
  }
`);

for (const key of keys) {
  watch(
    () => effectiveValue(key),
    (value) => {
      if (pending.value) return;
      const field = fields[key];
      if (field.draft === field.saved) field.draft = value;
      field.saved = value;
    },
  );
}

async function persist(key: Field, reset: boolean) {
  if (pending.value) return;
  const field = fields[key];
  if (!reset && field.draft === field.saved) return;
  pending.value = key;
  field.message = "";
  try {
    const result = await executeMutation({
      configPartial: { [key]: reset ? null : field.draft },
    });
    const config = result.data?.updateUserConfigPartial?.userData?.mergedConfig;
    if (
      result.error ||
      !config ||
      typeof config !== "object" ||
      Array.isArray(config)
    ) {
      throw new Error("Missing effective email configuration");
    }
    replaceUserMergedConfig(config);
    field.saved =
      typeof config[key] === "string" ? (config[key] as string) : "";
    field.draft = field.saved;
    field.error = false;
    field.message = t(`profile.emailPresets.${reset ? "resetDone" : "saved"}`);
    // Flush config watchers while pending so the other field's draft/baseline
    // cannot be changed by this field's successful save or reset.
    await nextTick();
  } catch {
    field.error = true;
    field.message = t("profile.emailPresets.error");
  } finally {
    pending.value = null;
  }
}

function cancel(key: Field) {
  fields[key].draft = fields[key].saved;
  fields[key].message = "";
}

function rememberSelection() {
  const element = bodyElement.value;
  if (element)
    selection = { start: element.selectionStart, end: element.selectionEnd };
}

async function insertLink() {
  const field = fields.default_email_body;
  const start = Math.min(selection.start, field.draft.length);
  const end = Math.min(selection.end, field.draft.length);
  field.draft = field.draft.slice(0, start) + marker + field.draft.slice(end);
  await nextTick();
  bodyElement.value?.focus();
  bodyElement.value?.setSelectionRange(
    start + marker.length,
    start + marker.length,
  );
  rememberSelection();
}
</script>

<template>
  <section class="email-presets" aria-labelledby="email-presets-title">
    <h2 id="email-presets-title">{{ t("profile.emailPresets.title") }}</h2>
    <p id="email-reset-hint" class="hint">
      {{ t("profile.emailPresets.resetHint") }}
    </p>
    <div v-for="key in keys" :key="key" class="preset-field">
      <label :for="key">{{
        t(
          `profile.emailPresets.${key === "default_email_subject" ? "subject" : "body"}`,
        )
      }}</label>
      <template v-if="key === 'default_email_body'">
        <div class="toolbar">
          <button
            type="button"
            class="button secondary"
            :disabled="!!pending"
            @mousedown.prevent
            @click="insertLink"
          >
            <span aria-hidden="true" class="material-symbols-outlined"
              >add</span
            >
            {{ t("profile.emailPresets.insert") }}
          </button>
        </div>
        <textarea
          :id="key"
          :ref="
            (element) => {
              bodyElement = element as HTMLTextAreaElement | null;
            }
          "
          v-model="fields[key].draft"
          class="textarea"
          :disabled="!!pending"
          rows="7"
          aria-describedby="email-link-hint"
          @select="rememberSelection"
          @keyup="rememberSelection"
          @click="rememberSelection"
          @blur="rememberSelection"
          @input="rememberSelection"
        />
        <p id="email-link-hint" class="hint">
          {{ t("profile.emailPresets.hint", { marker }) }}
        </p>
      </template>
      <input
        v-else
        :id="key"
        v-model="fields[key].draft"
        class="input"
        type="text"
        :disabled="!!pending"
      />
      <div class="actions">
        <button
          type="button"
          class="button primary"
          :disabled="!!pending || fields[key].draft === fields[key].saved"
          @click="persist(key, false)"
        >
          {{ t("profile.emailPresets.save") }}
        </button>
        <button
          type="button"
          class="button secondary"
          :disabled="!!pending || fields[key].draft === fields[key].saved"
          @click="cancel(key)"
        >
          {{ t("profile.emailPresets.cancel") }}
        </button>
        <button
          type="button"
          class="reset"
          aria-describedby="email-reset-hint"
          :disabled="!!pending"
          @click="persist(key, true)"
        >
          {{ t("profile.emailPresets.reset") }}
        </button>
      </div>
      <p
        v-if="fields[key].message"
        :role="fields[key].error ? 'alert' : 'status'"
        :class="{ error: fields[key].error }"
        class="feedback"
      >
        {{ fields[key].message }}
      </p>
    </div>
  </section>
</template>

<style scoped lang="scss">
.email-presets {
  margin-top: 2rem;
}
h2,
label {
  font-size: 0.9rem;
  font-weight: 500;
}
h2 {
  margin-bottom: 0.5rem;
}
label {
  display: block;
  margin-bottom: 0.4rem;
}
.preset-field {
  margin-top: 1.5rem;
}
.input,
.textarea {
  width: 100%;
  box-sizing: border-box;
}
.textarea {
  min-height: 10rem;
  resize: vertical;
}
.hint {
  font-size: 0.8rem;
  line-height: 1.5;
  opacity: 0.8;
  margin: 0.4rem 0;
}
.toolbar {
  margin-bottom: 0.6rem;
}
.reset {
  background: transparent;
  color: inherit;
  border: 0;
  cursor: pointer;
  font: inherit;
}
.actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.6rem;
}
.reset {
  margin-left: auto;
  text-decoration: underline;
  font-size: 0.8rem;
}
button:disabled {
  opacity: 0.5;
  cursor: default;
}
button:focus-visible {
  outline: 2px solid var(--classic-accent, #267e85);
  outline-offset: 3px;
}
.feedback {
  font-size: 0.85rem;
}
.error {
  color: var(--classic-error-text, #bd2020);
}
</style>
